import React, { useEffect, useState, useCallback } from 'react'
import { BooleanInput, TextInput, useGetList, required, SimpleForm,   useCreate,
  useNotify,
  useRedirect, } from 'react-admin'
import { useFormContext } from 'react-hook-form'
import PermissionTable from '../../components/domains/roles-permission/PermissionTable'
import { CreateBase } from '../../components/base'
import { Grid } from '@mui/material'
import { COMMON_FIELDS, ROLES_FIELDS } from '../../providers/localization/constant'
import { RolePermission } from './type'
import isEmpty from 'lodash/isEmpty'
import { registerPermissions } from '../../utils/user'
import { FormBottomToolbarWithPermission } from '../../components/form'
import { AllPermissionBooleanInput } from '../../components/domains/roles-permission'

const RESOURCE_PREFIX = 'resources.roles.create'

const RolesPermissionsCreateFormBody = (): JSX.Element => {
  const { data: permissionKeys } = useGetList('permissions', {
    sort: { field: 'key', order: 'ASC' },
  })
  const { register, getValues } = useFormContext()

  const [rolesPermission, setRolesPermission] = useState<RolePermission[] | []>([])
  const [isRegistering, setisRegistering] = useState(true)

  /**
   * Effect #1
   * - register permission to form state
   * */
  useEffect(() => {
    if (permissionKeys && register) {
      const registeredPermissions = registerPermissions(permissionKeys)
      register('permissions', {
        value: registeredPermissions,
      })
      setisRegistering(false)
    }
  }, [permissionKeys, register])

  /**
   * Effect #2
   * - set rolesPermission state value from getValues (form)
   * */
  useEffect(() => {
    if (!isRegistering) setRolesPermission(() => getValues()?.permissions || [])
  }, [getValues, isRegistering])

  if (isRegistering || isEmpty(permissionKeys) || isEmpty(rolesPermission)) return null

  return (
    <>
      <Grid container spacing={2} alignItems='center'>
        <Grid item xs={12} sm={4}>
          <TextInput
            source='name'
            label={`${ROLES_FIELDS}.name`}
            validate={[required()]}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <BooleanInput source='is_active' label={`${COMMON_FIELDS}.status.label`} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <AllPermissionBooleanInput />
        </Grid>
      </Grid>
      <PermissionTable rolePermissionList={rolesPermission} permissionKeys={permissionKeys} />
    </>
  )
}

const RolesPermissionsCreate = (): JSX.Element => {
  const notify = useNotify()
  const redirect = useRedirect()
  const onError = useCallback(
    (error): void => {
      const errorMessage = error.response.data.message
      return notify(errorMessage || 'ra.validation.create_roles_fail', { type: 'error' })
    },
    [notify],
  )
  const onSuccess = useCallback(async (): Promise<void> => {
    notify('ra.notification.create_roles_success', { type: 'success' })
    redirect('/roles')
  }, [notify, redirect])
  const [create] = useCreate(undefined, undefined, { onError, onSuccess })

  const handleSubmit = (data): void => {
    const newData = {
      ...data,
      permissions: data.permissions.map(permission => ({
          ...permission,
          is_active: true
      }))
    };
    create('roles', { data: newData })
  }
  return (
    <CreateBase
      title={`${RESOURCE_PREFIX}.title`}
      redirect='list'
      listAction={{
        hasList: true,
        hasBackButton: true,
      }}
    >
      <SimpleForm onSubmit={handleSubmit} toolbar={<FormBottomToolbarWithPermission />}>
        <RolesPermissionsCreateFormBody />
      </SimpleForm>
    </CreateBase>
  )
}

export default RolesPermissionsCreate
